import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState, useRef, useCallback, useEffect, useId, type ReactNode } from 'react'
import {
  Activity,
  Bell,
  Info,
  KeyRound,
  Monitor,
  Palette,
  ShieldCheck,
  SunMedium,
  Moon,
  Columns,
  Camera,
  Plus,
  Lock,
  Copy,
  Trash2,
} from 'lucide-react'

import { useTheme, type Theme } from '~/contexts/theme-context'
import {
  meQuery,
  meApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  type AdminApiKey,
} from '~/features/me'
import {
  ChangeAvatarDialog,
  ChangePasswordDrawer,
  ProfileSection,
  profileQuery,
  useUpdateProfileMutation,
  type FullUserProfile,
} from '~/features/profile'

/**
 * `/admin/profile` — the admin's personal workspace surface.
 *
 * This is the canonical Profile page from
 * `docs/admin-design-refinement/13-profile-and-settings.md` §C, ported in
 * to the admin-dashboard SPA. Everyone-accessible (the rail item is
 * visible for every actor) — only `/admin/settings` is super_admin-gated.
 *
 * Phase note: most sections render placeholder data. Sections wired to
 * live data:
 *   - Account: pulled from the `me` query (display name from email,
 *     email itself).
 *   - Theme: hooked up to `useTheme()` so toggling here flips the whole
 *     SPA's theme exactly like the rail toggle does.
 *
 * Sections that are placeholders (with intentional empty-state copy):
 *   - Notifications (4 toggle categories, no persistence yet)
 *   - Sessions (empty state — session listing endpoint pending)
 *   - API keys (empty state with disabled CTA — endpoint pending)
 *   - Security (link to /authentication/change-password is real, 2FA toggle
 *     is a placeholder)
 *   - Activity (empty state — admin_activity_log endpoint pending)
 *
 * When the storage proxy + sessions/keys/activity endpoints land, swap
 * the placeholder bodies for real queries — the section chrome stays.
 */
export const Route = createFileRoute('/admin/profile')({
  component: ProfilePage,
})

function ProfilePage(): ReactNode {
  const me = useQuery(meQuery())
  const profile = useQuery(profileQuery())
  const [avatarOpen, setAvatarOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)

  const email = profile.data?.email ?? me.data?.email ?? null

  return (
    <div className="ka-page">
      <div className="ka-canvas">
        <header className="ka-page-header">
          <div>
            <h1 className="ka-page-header-title">Profile</h1>
            <p className="ka-page-header-subtitle">
              Your personal workspace inside the admin app.
            </p>
          </div>
        </header>

        <AccountSection
          profile={profile.data ?? null}
          email={email}
          loading={profile.isLoading || me.isLoading}
          onChangeAvatar={() => setAvatarOpen(true)}
        />
        <ThemeSection />
        <NotificationsSection />
        <SessionsSection />
        <ApiKeysSection />
        <SecuritySection onChangePassword={() => setPasswordOpen(true)} />
        <ActivitySection />
      </div>

      <ChangeAvatarDialog
        open={avatarOpen}
        currentInitials={initialsFor(email)}
        onClose={() => setAvatarOpen(false)}
      />
      <ChangePasswordDrawer
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
      />
    </div>
  )
}

// ============================================================================
// Section 1 — Account
// ============================================================================

interface AccountSectionProps {
  readonly profile: FullUserProfile | null
  readonly email: string | null
  readonly loading: boolean
  readonly onChangeAvatar: () => void
}

function AccountSection({
  profile,
  email,
  loading,
  onChangeAvatar,
}: AccountSectionProps): ReactNode {
  const initials = initialsFor(email)
  const serverName = profile?.full_name ?? (email ? deriveDisplayName(email) : '')
  const [draft, setDraft] = useState<string>(serverName)
  const updateMutation = useUpdateProfileMutation()

  // Keep the input in sync when the server value changes (initial load,
  // after a successful save) but never overwrite an in-flight edit.
  useEffect(() => {
    if (!updateMutation.isPending) setDraft(serverName)
  }, [serverName, updateMutation.isPending])

  const trimmed = draft.trim()
  const dirty = trimmed.length > 0 && trimmed !== serverName.trim()

  const onSave = (): void => {
    if (!dirty) return
    updateMutation.mutate(
      { full_name: trimmed },
      {
        // No-op success/error; the mutation updates the cache, the
        // displayed banner reads `updateMutation.{isSuccess,isError}`.
      },
    )
  }

  return (
    <ProfileSection
      id="account"
      icon={Info}
      title="Account"
      description="Your profile shown across the admin app."
    >
      <div className="ka-profile-account">
        <div className="ka-profile-avatar-col">
          <button
            type="button"
            onClick={onChangeAvatar}
            aria-label="Change profile photo"
            className="ka-profile-avatar-btn"
          >
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt=""
                className="ka-profile-avatar-img"
              />
            ) : (
              <span aria-hidden="true" className="ka-profile-avatar-initials">
                {initials}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onChangeAvatar}
            className="ka-btn ka-btn--ghost ka-btn--sm"
          >
            <Camera size={12} aria-hidden="true" /> Change
          </button>
        </div>

        <div className="ka-profile-fields-col">
          <div className="ka-profile-field">
            <label
              htmlFor="profile-display-name"
              className="ka-profile-field-label"
            >
              Display name
            </label>
            <div className="ka-profile-field-edit-row">
              <input
                id="profile-display-name"
                type="text"
                className="ka-field-input"
                value={loading ? '' : draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={loading ? '…' : 'Your name'}
                disabled={loading || updateMutation.isPending}
              />
              <button
                type="button"
                className="ka-btn ka-btn--primary ka-btn--sm"
                onClick={onSave}
                disabled={
                  !dirty || updateMutation.isPending || loading
                }
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
            {updateMutation.isError ? (
              <div role="alert" className="ka-inline-alert">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'Failed to save profile.'}
              </div>
            ) : null}
          </div>
          <DefRow
            label="Email"
            value={loading ? '…' : (email ?? '—')}
            mono
          />
        </div>
      </div>
    </ProfileSection>
  )
}

interface DefRowProps {
  readonly label: string
  readonly value: ReactNode
  readonly mono?: boolean
}

function DefRow({ label, value, mono }: DefRowProps): ReactNode {
  return (
    <div className="ka-profile-field">
      <dt className="ka-profile-field-label">{label}</dt>
      <dd
        className="ka-profile-field-value"
        style={mono === true ? { fontFamily: 'var(--font-mono)' } : undefined}
      >
        {value}
      </dd>
    </div>
  )
}

// ============================================================================
// Section 2 — Theme
// ============================================================================

function ThemeSection(): ReactNode {
  const { theme, setTheme } = useTheme()
  const options: ReadonlyArray<{
    readonly value: Theme
    readonly label: string
    readonly icon: typeof SunMedium
  }> = [
    { value: 'light', label: 'Light', icon: SunMedium },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'split', label: 'Split', icon: Columns },
  ]

  return (
    <ProfileSection
      id="theme"
      icon={Palette}
      title="Theme"
      description="Switch between light, dark, and split-preview themes."
    >
      <div role="radiogroup" aria-label="Theme" className="ka-option-group">
        {options.map((opt) => {
          const Icon = opt.icon
          const active = theme === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setTheme(opt.value)}
              className={`ka-btn ${active ? 'ka-btn--primary' : 'ka-btn--secondary'}`}
            >
              <Icon size={14} aria-hidden="true" />
              {opt.label}
            </button>
          )
        })}
      </div>
    </ProfileSection>
  )
}

// ============================================================================
// Section 3 — Notifications (placeholder toggles)
// ============================================================================

interface NotificationCategory {
  readonly id: string
  readonly label: string
  readonly description: string
  readonly defaultOn: boolean
}

const NOTIFICATION_CATEGORIES: ReadonlyArray<NotificationCategory> = [
  {
    id: 'support',
    label: 'Support tickets assigned to me',
    description: 'New assignments and SLA-at-risk warnings.',
    defaultOn: true,
  },
  {
    id: 'billing',
    label: 'Subscription + billing alerts',
    description: 'Past-due, canceled, and refunded events.',
    defaultOn: true,
  },
  {
    id: 'fraud',
    label: 'Fraud + security signals',
    description: 'High-severity fraud flags raised on customer accounts.',
    defaultOn: true,
  },
  {
    id: 'system',
    label: 'Platform health',
    description: 'Service outages, degraded systems, failed cron jobs.',
    defaultOn: true,
  },
  {
    id: 'audit',
    label: 'Audit-log alerts',
    description: 'Bulk admin actions over configured thresholds.',
    defaultOn: false,
  },
]

function NotificationsSection(): ReactNode {
  const [state, setState] = useState<Readonly<Record<string, boolean>>>(() => {
    const seed: Record<string, boolean> = {}
    for (const c of NOTIFICATION_CATEGORIES) seed[c.id] = c.defaultOn
    return seed
  })

  return (
    <ProfileSection
      id="notifications"
      icon={Bell}
      title="Notifications"
      description="Choose which platform events email you. Per-channel matrix lands with the alerts API."
      aside={
        <span className="ka-chip ka-chip--muted">Preview</span>
      }
    >
      <ul className="ka-setting-list">
        {NOTIFICATION_CATEGORIES.map((cat) => {
          const checked = state[cat.id] ?? cat.defaultOn
          const inputId = `notify-${cat.id}`
          return (
            <li key={cat.id} className="ka-setting-row">
              <div className="ka-setting-row-body">
                <label htmlFor={inputId} className="ka-setting-row-label">
                  {cat.label}
                </label>
                <p className="ka-setting-row-desc">{cat.description}</p>
              </div>
              <input
                id={inputId}
                type="checkbox"
                role="switch"
                checked={checked}
                onChange={(event) =>
                  setState((prev) => ({
                    ...prev,
                    [cat.id]: event.target.checked,
                  }))
                }
                aria-label={cat.label}
                className="ka-switch"
              />
            </li>
          )
        })}
      </ul>
    </ProfileSection>
  )
}

// ============================================================================
// Section 4 — Sessions (placeholder empty state)
// ============================================================================

function SessionsSection(): ReactNode {
  return (
    <ProfileSection
      id="sessions"
      icon={Monitor}
      title="Sessions"
      description="Active sign-ins to the admin app. Revoke any device that isn't yours."
    >
      <EmptyState
        title="No other active sessions"
        body="You're only signed in on this device. When the sessions endpoint ships, every device with a live admin session will appear here with a revoke action."
      />
    </ProfileSection>
  )
}

// ============================================================================
// Section 5 — API Keys
// ============================================================================

function ApiKeysSection(): ReactNode {
  const [creating, setCreating] = useState(false)
  const [newKey, setNewKey] = useState<{ name: string; plaintext: string } | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<AdminApiKey | null>(null)
  const keys = useQuery(meApiKeysQuery())
  const createMutation = useCreateApiKeyMutation()
  const revokeMutation = useRevokeApiKeyMutation()

  const handleCreate = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const fd = new FormData(event.currentTarget)
      const name = (fd.get('key-name') as string | null)?.trim() ?? ''
      if (!name) return
      const result = await createMutation.mutateAsync({ name, scopes: [] })
      event.currentTarget.reset()
      setNewKey({ name: result.key.name, plaintext: result.plaintext })
      setCreating(false)
    },
    [createMutation],
  )

  const handleRevoke = useCallback(
    async () => {
      if (!revokeTarget) return
      await revokeMutation.mutateAsync(revokeTarget.id)
      setRevokeTarget(null)
    },
    [revokeMutation, revokeTarget],
  )

  return (
    <ProfileSection
      id="keys"
      icon={KeyRound}
      title="API keys"
      description="Personal access tokens for scripts and CI. Available to super-admins."
      aside={
        <button
          type="button"
          className="ka-btn ka-btn--primary ka-btn--sm"
          onClick={() => setCreating(true)}
          disabled={creating || newKey !== null}
        >
          <Plus size={12} aria-hidden="true" /> Create API key
        </button>
      }
    >
      {keys.isPending && (
        <div className="ka-empty" role="status" aria-busy="true">
          <div className="ka-empty-body">Loading keys…</div>
        </div>
      )}
      {keys.isError && (
        <div role="alert" className="ka-inline-alert">
          {keys.error instanceof Error ? keys.error.message : 'Failed to load keys.'}
        </div>
      )}
      {keys.isSuccess && keys.data.length === 0 && !creating && (
        <EmptyState
          title="No API keys yet"
          body="Create a personal access token to authenticate scripts and CI jobs."
        />
      )}
      {keys.isSuccess && keys.data.length > 0 && (
        <table className="ka-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.data.map((key) => (
              <tr key={key.id}>
                <td className="ka-user-name">{key.name}</td>
                <td>
                  <code className="ka-mono-meta">{key.key_prefix}…</code>
                </td>
                <td className="ka-td-meta">
                  {key.last_used_at
                    ? new Date(key.last_used_at).toLocaleDateString()
                    : '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="ka-navbar-icon-btn"
                    aria-label={`Revoke ${key.name}`}
                    onClick={() => setRevokeTarget(key)}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <form onSubmit={handleCreate}>
          <div className="ka-detail-page-row">
            <div className="ka-field-input-wrap">
              <input
                id="key-name"
                name="key-name"
                type="text"
                className="ka-field-input"
                placeholder="e.g. CI deploy token"
                required
              />
            </div>
            <button
              type="submit"
              className="ka-btn ka-btn--primary ka-btn--sm"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              className="ka-btn ka-btn--ghost ka-btn--sm"
              onClick={() => setCreating(false)}
            >
              Cancel
            </button>
          </div>
          {createMutation.isError && (
            <div role="alert" className="ka-inline-alert">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed to create key.'}
            </div>
          )}
        </form>
      )}

      {newKey && (
        <NewKeyBanner
          name={newKey.name}
          plaintext={newKey.plaintext}
          onDismiss={() => setNewKey(null)}
        />
      )}

      {revokeTarget && (
        <RevokeKeyDialog
          keyName={revokeTarget.name}
          revoking={revokeMutation.isPending}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </ProfileSection>
  )
}

interface NewKeyBannerProps {
  readonly name: string
  readonly plaintext: string
  readonly onDismiss: () => void
}

function NewKeyBanner({ name, plaintext, onDismiss }: NewKeyBannerProps): ReactNode {
  const textRef = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(plaintext)
      textRef.current?.select()
      setCopied(true)
      setCopyError(false)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyError(true)
    }
  }, [plaintext])

  return (
    <div className="ka-banner is-tip" role="alert">
      <div className="ka-banner-body">
        <strong>Key created: {name}</strong>
        <p className="ka-banner-desc">
          Copy this token now — it won&rsquo;t be shown again.
        </p>
        <div className="ka-detail-page-row">
          <div className="ka-field-input-wrap">
            <input
              ref={textRef}
              type="text"
              readOnly
              className="ka-field-input ka-mono-meta"
              value={plaintext}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <button
            type="button"
            className={`ka-btn ka-btn--sm ${copied ? 'ka-btn--primary' : 'ka-btn--secondary'}`}
            onClick={copy}
            aria-label="Copy API key"
          >
            <Copy size={12} aria-hidden="true" /> {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        {copyError && (
          <div role="alert" className="ka-inline-alert">
            Clipboard access denied — please select and copy the key manually.
          </div>
        )}
      </div>
      <button
        type="button"
        className="ka-btn ka-btn--ghost ka-btn--sm"
        onClick={onDismiss}
      >
        Dismiss
      </button>
    </div>
  )
}

interface RevokeKeyDialogProps {
  readonly keyName: string
  readonly revoking: boolean
  readonly onConfirm: () => void
  readonly onCancel: () => void
}

function RevokeKeyDialog({
  keyName,
  revoking,
  onConfirm,
  onCancel,
}: RevokeKeyDialogProps): ReactNode {
  const titleId = useId()
  return (
    <div
      role="presentation"
      className="ka-overlay-center"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="ka-modal-panel">
        <header className="ka-panel-head">
          <h2 id={titleId} className="ka-panel-head-title">Revoke API key</h2>
        </header>
        <div className="ka-panel-body">
          <p>
            Revoke <strong>{keyName}</strong>? Any scripts using this token will stop
            working immediately. This cannot be undone.
          </p>
        </div>
        <footer className="ka-panel-foot">
          <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--destructive ka-btn--sm"
            onClick={onConfirm}
            disabled={revoking}
          >
            {revoking ? 'Revoking…' : 'Revoke key'}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ============================================================================
// Section 6 — Security (real change-password link + 2FA placeholder)
// ============================================================================

interface SecuritySectionProps {
  readonly onChangePassword: () => void
}

function SecuritySection({
  onChangePassword,
}: SecuritySectionProps): ReactNode {
  const [twoFA, setTwoFA] = useState(false)

  return (
    <ProfileSection
      id="security"
      icon={ShieldCheck}
      title="Security"
      description="Password and two-factor authentication."
    >
      <div className="ka-setting-list">
        <div className="ka-setting-row">
          <Lock size={16} aria-hidden="true" className="ka-icon-muted" />
          <div className="ka-setting-row-body">
            <div className="ka-setting-row-label">Password</div>
            <p className="ka-setting-row-desc">
              Verified through the auth flow at{' '}
              <Link
                to="/authentication/change-password"
                className="ka-link"
              >
                /authentication/change-password
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={onChangePassword}
            className="ka-btn ka-btn--secondary ka-btn--sm"
          >
            Change password
          </button>
        </div>

        <div className="ka-setting-row">
          <ShieldCheck size={16} aria-hidden="true" className="ka-icon-muted" />
          <div className="ka-setting-row-body">
            <label
              htmlFor="twofa-toggle"
              className="ka-setting-row-label"
            >
              Two-factor authentication (TOTP)
            </label>
            <p className="ka-setting-row-desc">
              Authenticator-app based 2FA. Setup ships with the security
              endpoint — toggle is a placeholder today.
            </p>
          </div>
          <input
            id="twofa-toggle"
            type="checkbox"
            role="switch"
            checked={twoFA}
            onChange={(event) => setTwoFA(event.target.checked)}
            aria-label="Two-factor authentication"
            className="ka-switch"
          />
        </div>
      </div>
    </ProfileSection>
  )
}

// ============================================================================
// Section 7 — Activity (placeholder empty state)
// ============================================================================

function ActivitySection(): ReactNode {
  return (
    <ProfileSection
      id="activity"
      icon={Activity}
      title="Activity"
      description="Your recent admin actions, drawn from admin_activity_log."
    >
      <EmptyState
        title="No activity recorded yet"
        body="Once you take an action that writes to admin_activity_log (granting a plan, refunding a charge, locking an account, etc.) it will appear here."
      />
    </ProfileSection>
  )
}

// ============================================================================
// Helpers
// ============================================================================

interface EmptyStateProps {
  readonly title: string
  readonly body: string
}

function EmptyState({ title, body }: EmptyStateProps): ReactNode {
  return (
    <div className="ka-empty">
      <p className="ka-empty-title">{title}</p>
      <p className="ka-empty-body">{body}</p>
    </div>
  )
}

function initialsFor(email: string | null): string {
  if (!email) return 'AD'
  const local = email.split('@')[0] ?? email
  const parts = local.split(/[._-]/).filter((part) => part.length > 0)
  if (parts.length === 0) return 'AD'
  if (parts.length === 1) {
    const only = parts[0] ?? ''
    return only.slice(0, 2).toUpperCase()
  }
  const first = parts[0] ?? ''
  const second = parts[1] ?? ''
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase()
}

function deriveDisplayName(email: string): string {
  const local = email.split('@')[0] ?? email
  return local
    .split(/[._-]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
